import { z } from 'zod';

// ── Роли ──
export const ROLES = [
  'admin',
  'manager',
  'dispatcher',
  'shtab',
  'rukstroy',
  'commandant',
  /**
   * Площадка (ADR 0112) — целевая роль реформы доступа: одна объектная роль вместо трёх. Заведена
   * до перевода учёток и до этого перевода никому не назначена: наборы «Заказ техники» и «Виза
   * объекта» объявляют её совместимой ролью (`grant_roles`), а строку с несуществующим значением
   * enum база не примет — значит роль обязана существовать раньше каталога, который на неё
   * ссылается.
   */
  'site',
  'department',
  'department_head',
  'operator',
  'observer',
  // Водитель (ADR 0102) — работник справочника, получивший вход в свой кабинет. Прав основного
  // портала у роли нет вовсе: она открывает `/driver` и ничего больше.
  'driver',
  // Служба главного механика: обе роли смотрят парк целиком и своей оси области не имеют — ОГМ
  // отвечает за технику компании, а не за площадку. Различаются одним: главный механик ведёт
  // водителей и списывает испорченные бланки, механик только смотрит.
  'mechanic',
  'chief_mechanic',
] as const;
export const roleSchema = z.enum(ROLES);
export type Role = (typeof ROLES)[number];

export const roleLabels: Record<Role, string> = {
  admin: 'Администратор',
  manager: 'Менеджер',
  dispatcher: 'Диспетчер',
  shtab: 'Штаб',
  rukstroy: 'Руководитель строительства',
  // Комендант — третий заказчик на объекте, но только по мусору: должность на площадке так и
  // называется, и подпись повторяет её, а не описывает набор прав.
  commandant: 'Комендант',
  // «Площадка», а не «Сотрудник объекта»: подпись называет место работы, потому что должностей за
  // ролью стоит несколько — штаб, руководитель строительства и комендант различаются не ролью, а
  // выданными полномочиями (план §9). Назови роль одной из должностей — и две другие читались бы
  // в списке учёток как понижение.
  site: 'Площадка',
  // Подписи по образцу «Штаба»: роль называет подразделение, а не должность внутри него.
  department: 'Отдел',
  department_head: 'Руководитель отдела',
  // Модуль в подписи не назван намеренно: что именно исполняет учётка — вывоз мусора или
  // аренду техники — решает тип её контрагента, а не роль (ADR 0038).
  operator: 'Оператор (внешний исполнитель)',
  observer: 'Наблюдатель',
  // Должность в подписи не названа: кабинет открыт машинисту и трактористу наравне с водителем
  // (ADR 0102), а «водитель» — как эту роль зовут в парке.
  driver: 'Водитель',
  // Должность, как её зовут в парке; «главный механик» — он же глава ОГМ, и подпись повторяет
  // штатное название, а не перечисляет права.
  mechanic: 'Механик',
  chief_mechanic: 'Главный механик',
};

/**
 * Подписи ролей, упразднённых реформой доступа, — словарь, переживающий сами роли (план
 * реструктуризации §13.2 «след, который не переписать»; ADR 0113).
 *
 * **Зачем отдельный словарь, а не строка в `roleLabels`.** Упразднение роли — это снятие значения
 * из `ROLES` (шаг cleanup этапов 8 и 9), после которого `Record<Role, string>` перестаёт её
 * принимать: тип обязан отвечать за действующие роли и только за них, иначе форма учётки предложит
 * администратору выбрать «Штаб». А знать подпись всё равно нужно — значение никуда не делось.
 *
 * **Где оно остаётся.** Значение enum'а в Postgres не удаляется вовсе (`ALTER TYPE ... DROP VALUE`
 * не существует), и на нём держится `user_role_migration.role_before` — единственный ответ на
 * «кем человек был до перевода», по которому этап откатывается. Роль записана задним числом и в
 * `audit_log.metadata` (append-only), и в текстах отправленных писем.
 *
 * **Сегодня словарь пуст, и это правильное состояние, а не заготовка.** Пуст он ровно до релиза
 * cleanup: тогда четыре подписи переезжают сюда из `roleLabels` тем же коммитом, которым имена
 * уходят из `ROLES`. Заведён заранее затем, что вместе с ним заводится страж (`role-labels`
 * в `apps/api/test/role-migration-pending.db.test.ts`): он требует подпись **на каждое значение
 * enum'а в базе**, а не только на действующую роль, — и покраснеет на cleanup'е, забывшем перенос.
 * Появись словарь тем же релизом, что и перенос, страж проверял бы работу, сделанную минуту назад.
 *
 * Тип ключа — `string`, а не `Role`: в том и смысл, что ключи здесь — значения, которых в `Role`
 * уже нет.
 */
export const ARCHIVED_ROLE_LABELS: Readonly<Record<string, string>> = {};

/**
 * Подпись роли по значению из базы — действующей или упразднённой.
 *
 * Спрашивают этой функцией там, где роль приходит **историей**, а не выбором в форме: журнал
 * изменений учёток (ADR 0109), снимок перевода, письма. Прямой `roleLabels[role]` там ответил бы
 * `undefined` в день, когда роль упразднят, и в журнале вместо «Штаб» появилась бы пустота — в
 * записи, которую уже не переписать.
 *
 * Где роль приходит из формы и типизирована `Role`, обращение остаётся прямым: там подпись обязана
 * существовать, и `undefined` означал бы забытую строку словаря, а не историю.
 *
 * Неизвестное значение возвращается как есть, а не «—»: строка вроде `shtab` в журнале читается
 * хуже подписи, но лучше прочерка — по ней хотя бы видно, о чём речь.
 */
export function roleTitle(role: string): string {
  return roleLabels[role as Role] ?? ARCHIVED_ROLE_LABELS[role] ?? role;
}

export const roleColors: Record<Role, string> = {
  admin: 'magenta',
  manager: 'geekblue',
  dispatcher: 'cyan',
  shtab: 'orange',
  rukstroy: 'purple',
  // Комендант работает на той же площадке, что штаб, но в одном модуле — цвет своей, отдельной
  // пометки: в списке учёток эти две роли различают глазами.
  commandant: 'lime',
  // Свой цвет, а не оранжевый штаба: до этапа 8 обе роли живут рядом, и одинаковая пометка в
  // списке учёток означала бы, что перевод уже случился. `pink` — последний свободный пресет
  // палитры: остальные двенадцать разобраны ролями выше, и повтор читался бы как «та же служба».
  site: 'pink',
  // Отдел — заказчик со стороны офиса; цвета своей пары, но той же логики «сотрудник — светлее,
  // руководитель — насыщеннее», что у штаба и руководителя строительства.
  department: 'gold',
  department_head: 'volcano',
  operator: 'green',
  // Серый: роль ничего не ведёт, и в списке учёток она не должна спорить с теми, кто ведёт.
  observer: 'default',
  // Синий — цвет рейса в гараже: водитель и есть тот, кто в этом рейсе едет.
  driver: 'blue',
  // Пара службы главного механика — та же логика, что у штаба с руководителем: сотрудник светлее,
  // главный насыщеннее. Цвета свободные: `gold` и `volcano` уже заняты отделом, и повтор в списке
  // учёток читался бы как «та же служба».
  mechanic: 'yellow',
  chief_mechanic: 'red',
};

/**
 * Роли, работающие в пределах одного объекта строительства (ADR 0025): и видимость заявок,
 * и правки ограничены объектом учётки, поэтому объект у них обязателен — без него роль не
 * ограничена ничем и одновременно не видит ничего. Список здесь, а не в проверках доступа:
 * по нему и API требует объект при активации, и форма учётки показывает поле.
 *
 * Четыре имени вместо трёх — состояние перехода, а не целевое (план §11): `site` (ADR 0112) заведена
 * до перевода учёток, и до этапа 8 старые три живут рядом с ней. После перевода список сожмётся до
 * одной роли, и ось наконец совпадёт с ролью один к одному — как у `PERSON_SCOPED_ROLES` сегодня.
 *
 * Строка `site` здесь — единственное, что понадобилось слою области от новой роли: правила недельных
 * заявок спрашивают ось (`roleScopeAxis`), а не имя роли, и оттого приняли её без правки кода.
 */
export const OBJECT_SCOPED_ROLES = ['shtab', 'rukstroy', 'commandant', 'site'] as const;

export function isObjectScopedRole(role: Role | null | undefined): boolean {
  return !!role && (OBJECT_SCOPED_ROLES as readonly string[]).includes(role);
}

/**
 * Роли, работающие в пределах отдела (ADR 0040). Отдел — офисное подразделение, и с объектами
 * строительства он не пересекается: учётка привязана либо к объектам, либо к отделам, третьего
 * не дано. Поэтому это не расширение объектной оси, а вторая такая же — со своим списком.
 *
 * Список здесь, рядом с объектными ролями и ролями от контрагента, по той же причине: по нему
 * API требует отделы при активации, а форма учётки показывает поле.
 */
export const DEPARTMENT_SCOPED_ROLES = ['department', 'department_head'] as const;

export function isDepartmentScopedRole(role: Role | null | undefined): boolean {
  return !!role && (DEPARTMENT_SCOPED_ROLES as readonly string[]).includes(role);
}

/**
 * Работает ли роль в пределах подразделения — объекта или отдела. Нужен там, где правило одно на
 * обе оси: «правит заявку только пока она Новая», «область обязательна при активации», «поле
 * области в форме учётки». Перечислять две оси в каждом таком месте значит забыть одну из них
 * при появлении третьей.
 */
export function isPlaceScopedRole(role: Role | null | undefined): boolean {
  return isObjectScopedRole(role) || isDepartmentScopedRole(role);
}

/**
 * Роли, работающие от контрагента (ADR 0038): контрагент задаёт им и область видимости («чьи
 * заявки видны»), и сам модуль работы — он следует из типа контрагента. Отсюда обязательность
 * контрагента при активации и запрет менять тип контрагента, пока на нём висят учётки.
 *
 * Список здесь, рядом с объектными ролями, по той же причине: по нему API требует контрагента,
 * форма учётки показывает поле, а матрица прав знает, у кого спрашивать тип контрагента.
 */
export const COUNTERPARTY_SCOPED_ROLES = ['operator'] as const;

/**
 * Роли, работающие от карточки человека (ADR 0102) — четвёртая ось области после объектов,
 * отделов и контрагента, и самая узкая: видно только то, что про самого работника.
 *
 * Список здесь, рядом с остальными осями, по той же причине: по нему форма учётки требует
 * человека при активации, а API — при восстановлении из архива. Отличие от прочих осей одно:
 * область не описывается предикатом в `lib/access.ts`, она описывается отсутствием параметра —
 * `personId` кабинет не принимает вовсе и берёт из принципала.
 */
export const PERSON_SCOPED_ROLES = ['driver'] as const;

export function isPersonScopedRole(role: Role | null | undefined): boolean {
  return !!role && (PERSON_SCOPED_ROLES as readonly string[]).includes(role);
}

export function isCounterpartyScopedRole(role: Role | null | undefined): boolean {
  return !!role && (COUNTERPARTY_SCOPED_ROLES as readonly string[]).includes(role);
}

// Кто что может — в permissions.ts (ADR 0021): здесь только словари статусов и типов, чтобы
// списки ролей не расходились по файлам.

// ── Статусы заявки ──
//
// Порядок значений здесь — порядок цикла, и он же порядок значений enum'а в базе: столбец
// «Статус» сортируется по нему, и «Завершена» обязана стоять между «Выполнена» и «Отменена»
// (миграция 0194 добавляет значение `BEFORE 'cancelled'` ровно поэтому).
export const REQUEST_STATUSES = ['new', 'confirmed', 'done', 'completed', 'cancelled'] as const;
export const requestStatusSchema = z.enum(REQUEST_STATUSES);
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

// Значение `confirmed` осталось от прежней формулировки статуса; переименовалась только подпись.
export const requestStatusLabels: Record<RequestStatus, string> = {
  new: 'Новая',
  confirmed: 'В работе',
  done: 'Выполнена',
  // Вывоз мусора (ADR 0135): бумага разобрана, сверка сошлась, спрашивать по заявке больше нечего.
  // «Выполнена» отвечает на «вывезли», «Завершена» — на «приняли»; у заказа техники этого статуса
  // не бывает вовсе, и подпись говорит про заявку вообще, а не про модуль.
  completed: 'Завершена',
  cancelled: 'Отменена',
};

export const requestStatusColors: Record<RequestStatus, string> = {
  new: 'blue',
  confirmed: 'gold',
  done: 'green',
  // Не второй оттенок зелёного: «Выполнена» и «Завершена» стоят в одном столбце друг под другом,
  // и различать их надо с одного взгляда. `cyan` свободен и в подписях типов заявок — иначе тег
  // статуса слился бы с соседним тегом типа в той же строке.
  completed: 'cyan',
  cancelled: 'red',
};

/**
 * Модуль заявки. Статусный тип у «Вывоза мусора» и «Заказа ТС» общий — цикл у них один и тот же
 * до дня, когда бумагу стало нужно принимать: с ADR 0135 у вывоза за «Выполнена» идёт
 * «Завершена», а у техники «Выполнена» так и осталась терминальной.
 *
 * Отдельный тип, а не «флаг вывоза», потому что спрашивают его трое и по-разному: коридор
 * переходов, откаты и матрица прав. Строкой-именем модуля, а не типом заявки: у вывоза их пять,
 * у техники два, и цикл не зависит ни от одного из них.
 *
 * Третьим пришла механизация (план `docs/mechanization-module-plan.md`, Р2): аренда малой
 * механизации ведётся тем же статусным типом, и «Завершена» у неё не бывает вовсе — как и у
 * техники. Модуль здесь обязателен ещё и потому, что с Р9 по нему спрашивается право статуса
 * (`REQUEST_STATUS_PERMISSION` в `permissions.ts`): дизъюнкция двух чужих прав третьему модулю не
 * открыла бы ни одного хода.
 */
export const REQUEST_MODULES = ['waste', 'vehicle', 'mech'] as const;
export type RequestModule = (typeof REQUEST_MODULES)[number];

/**
 * Рабочий цикл заявки линейный: «Новая» → «В работе» → «Выполнена». Отменить можно только
 * незакрытую заявку; «Отменена» терминальна — назад заявку не возвращают.
 *
 * Дальше циклы модулей расходятся (ADR 0135). У вывоза мусора «Выполнена» означает «вывезли и
 * предъявили талон», но не «бумага принята»: талоны разбирают и сверяют уже после закрытия
 * (ADR 0114), и до конца разбора заявка остаётся рабочей. Точку ставит «Завершена» — она и
 * терминальна. У заказа техники разбирать нечего, и «Выполнена» терминальна сама.
 *
 * Таблица на модуль, а не общая с оговоркой в коде: в неё смотрят и сервер, и портал, и оба
 * обязаны видеть один и тот же коридор.
 */
export const requestStatusTransitions: Record<
  RequestModule,
  Record<RequestStatus, RequestStatus[]>
> = {
  waste: {
    new: ['confirmed', 'cancelled'],
    confirmed: ['done', 'cancelled'],
    done: ['completed'],
    completed: [],
    cancelled: [],
  },
  vehicle: {
    new: ['confirmed', 'cancelled'],
    confirmed: ['done', 'cancelled'],
    done: [],
    // Недостижимо: значение статуса общее, а перехода в него у техники нет ни одного. Строка
    // здесь не заготовка, а обязанность типа — `Record` требует ответа на каждый статус, и
    // пустой список отвечает «отсюда никуда», что для заявки техники и верно.
    completed: [],
    cancelled: [],
  },
  /*
   * Механизация (план `docs/mechanization-module-plan.md`, Р2): коридор буква в букву как у
   * техники — «Выполнена» терминальна, разбирать после неё нечего. «Завершена» здесь не просто
   * недостижима, а запрещена: у модуля стоит `CHECK (status <> 'completed')` (Р8, по образцу
   * миграции 0195), потому что коридор — это код, а колонка принимает любое значение типа.
   *
   * Чего коридор **не** описывает: выдачу и возврат. Аренда идёт внутри «В работе» и различается
   * полями `actual_from`/`actual_to` (Р2) — четвёртого статуса заказчик не называл, а выдумывать
   * его значило бы завести у модуля свой статусный тип ради двух дат.
   */
  mech: {
    new: ['confirmed', 'cancelled'],
    confirmed: ['done', 'cancelled'],
    done: [],
    completed: [],
    cancelled: [],
  },
};

/**
 * Откат заявки назад по циклу — право администратора: взяли в работу, закрыли или отменили по
 * ошибке, а завести новую заявку вместо исправления означало бы потерять её номер и историю.
 *
 * Возврат из «В работе» в «Новую» отличается от остальных откатов тем, что **стирает работу**
 * (`transitionResetsWork`): назначенную технику, факт и рейс. Прочие откаты, наоборот,
 * прежние данные берегут — повторное закрытие обходится уже предъявленным фактом.
 *
 * Визы это не касается ни у одного из откатов (ADR 0172): согласие площадки не подчинено ходу
 * заявки, и снимают его своими дверями — отзывом и правкой по существу.
 *
 * Откат «Завершена» → «Выполнена» (ADR 0135) из тех, что берегут: завершение ничего не стирает,
 * оно лишь объявляет разбор законченным, — и возврат снова открывает талоны для правки.
 */
export const requestStatusRollbacks: Record<
  RequestModule,
  Record<RequestStatus, RequestStatus[]>
> = {
  waste: {
    new: [],
    confirmed: ['new'],
    done: ['confirmed'],
    completed: ['done'],
    cancelled: ['new'],
  },
  vehicle: {
    new: [],
    confirmed: ['new'],
    done: ['confirmed'],
    completed: [],
    cancelled: ['new'],
  },
  /*
   * Механизация: те же откаты, что у техники, — и оба у неё под собственным барьером состояния
   * (Р2). `confirmed → new` стирает договорённость и факт по построению (`new_empty_check`), и
   * потому запрещён, пока техника выдана: иначе действующая аренда уходила бы в «Новую», теряла
   * все следы и становилась физически удаляемой. Лечение ошибочной выдачи одно — сначала снять
   * отметку, потом откатывать.
   *
   * Таблица о состоянии строки не знает и знать не может: она отвечает «что за чем идёт», а
   * «можно ли отсюда» решает сервер по самой заявке (`mechTransitionBlocker` в `mech-requests.ts`).
   */
  mech: {
    new: [],
    confirmed: ['new'],
    done: ['confirmed'],
    completed: [],
    cancelled: ['new'],
  },
};

/**
 * Стирает ли переход всё, что заявка нажила в работе.
 *
 * Такой переход один — возврат «В работе» → «Новая». Заявка после него должна выглядеть как
 * заявка, которую ещё не начали выполнять: иначе «Новая» с назначенной машиной и рейсом означала
 * бы, что заявку и не снимали с работы. Отмена так не поступает — отменённая заявка остаётся
 * историей того, чем её собирались выполнять, и её ещё могут откатить обратно.
 *
 * Виза в этот перечень не входит (ADR 0172): «заведена и ждёт визы» — не то же самое, что
 * «заведена», и заявка, которую площадка согласовала однажды, остаётся согласованной, пока
 * согласие не отозвали или пока её не переписали по существу.
 *
 * Одна функция на портал и API: сервер по ней решает, что стереть, портал — что перечислить
 * человеку до нажатия. Разойдись они — сброс оказался бы неожиданностью.
 */
export function transitionResetsWork(from: RequestStatus, to: RequestStatus): boolean {
  return from === 'confirmed' && to === 'new';
}

/**
 * Стирает ли переход договорённость аренды механизации — арендодателя, ставку и единицу (Р8).
 *
 * Своя функция, а не строка в `transitionResetsWork`: тот описывает **один** переход
 * (`confirmed → new`) сразу для вывоза и техники, и трогать его нельзя — у соседей стирается
 * другое (назначенная машина, факт, рейс, виза) и по другому поводу. У механизации правило шире:
 * договорённости не бывает **ни при каком** входе в «Новую», это держит
 * `mech_requests_new_empty_check`, и цепочка `confirmed → cancelled → new` обязана оставить
 * заявку такой же пустой, как прямой откат.
 *
 * `from !== 'new'` — не педантизм: перехода «Новая → Новая» коридор не даёт, и без этого условия
 * функция отвечала бы «стирает» на несуществующий ход, то есть предупреждала бы человека о
 * потере там, где терять нечего.
 *
 * Одна функция на сервер и портал: сервер ею чистит поля, портал — предупреждает до нажатия.
 */
export function mechTransitionResetsDeal(from: RequestStatus, to: RequestStatus): boolean {
  return to === 'new' && from !== 'new';
}

/**
 * Единица ставки аренды механизации (Р7): цена договаривается за час либо за смену, и третьей
 * единицы нет намеренно — за что не назначена ставка, то нечем и считать (то же правило, что у
 * ставок техники, ADR 0018 и `VEHICLE_WORK_UNITS`).
 *
 * Единственное число (`hour`, а не `hours`), потому что значение отвечает на «за что ставка», а
 * не «чем меряют отработанное»: у соседнего модуля это две разные пары полей, а здесь одна
 * колонка `rate_unit` служит и ставке, и факту — `actual_units` обязан прийти в тех же единицах.
 * Значения совпадают с enum'ом базы `mech_rate_unit`.
 */
export const MECH_RATE_UNITS = ['hour', 'shift'] as const;
export const mechRateUnitSchema = z.enum(MECH_RATE_UNITS);
export type MechRateUnit = (typeof MECH_RATE_UNITS)[number];

/** Единица ставки в списке выбора и в карточке: «час», «смена». */
export const mechRateUnitLabels: Record<MechRateUnit, string> = {
  hour: 'час',
  shift: 'смена',
};

/** Подпись ставки в форме и карточке: «за час» / «за смену» — как у ставок техники (ADR 0027). */
export const mechRateUnitRateLabels: Record<MechRateUnit, string> = {
  hour: 'за час',
  shift: 'за смену',
};

/**
 * Единственный переход оператора (ADR 0010): взятую в работу заявку закрывает тот, кто её
 * выполнил. Подтверждать, отменять и откатывать заявки оператор не может — это решения заказчика.
 */
export const OPERATOR_STATUS_TRANSITIONS: Record<RequestStatus, RequestStatus[]> = {
  new: [],
  confirmed: ['done'],
  // Завершение заявки исполнителю не открыто и открыто не будет: «Завершена» означает, что бумагу
  // приняли, а приносит её он сам (ADR 0135). Таблица остаётся общей на оба модуля — коридор
  // исполнителя от модуля не зависит.
  done: [],
  completed: [],
  cancelled: [],
};

/**
 * Отмена заявки требует причины: заявка закрывается без результата, и без объяснения
 * ни автор, ни следующий диспетчер не поймут, почему.
 *
 * Того же требует возврат в «Новую» из работы: он стирает назначенную технику, факт и рейс, и
 * без объяснения в истории осталась бы пара переходов, по которой не понять, что случилось —
 * ошиблись машиной, отказался исполнитель или заявку завели не тому объекту. Исходный статус
 * необязателен: схема тела запроса знает только целевой (`from` ей взять неоткуда), а сервер и
 * портал спрашивают с обоими.
 */
export function statusChangeRequiresReason(to: RequestStatus, from?: RequestStatus): boolean {
  return to === 'cancelled' || (!!from && transitionResetsWork(from, to));
}

// ── Типы заявок (операции с контейнерами / вывоз) ──
export const REQUEST_TYPES = [
  'container_install',
  'container_replace',
  'container_removal',
  'waste_removal',
  'metal_removal',
] as const;
export const requestTypeSchema = z.enum(REQUEST_TYPES);
export type RequestType = (typeof REQUEST_TYPES)[number];

export const requestTypeLabels: Record<RequestType, string> = {
  container_install: 'Установка нового контейнера',
  container_replace: 'Замена полного контейнера на пустой',
  container_removal: 'Снятие контейнера (вывоз без замены)',
  waste_removal: 'Вывоз мусора (разовый объём)',
  metal_removal: 'Вывоз металлолома',
};

export const requestTypeColors: Record<RequestType, string> = {
  container_install: 'green',
  container_replace: 'gold',
  container_removal: 'volcano',
  waste_removal: 'blue',
  metal_removal: 'purple',
};

/**
 * Буква типа в номере заявок, заведённых до префикса «М-» (№ = «<num>-<буква>», миграция 0064).
 * Новых таких номеров не появляется — суффикс остаётся только у прежних заявок.
 *
 * У металлолома (ADR 0067) буква не используется ни разу: тип появился после префикса, и все его
 * заявки нумеруются «М-<num>». Стоит она здесь только потому, что перечень полон по типам —
 * пропуск означал бы, что у какого-то типа номера нет вовсе.
 */
export const requestTypeShort: Record<RequestType, string> = {
  container_install: 'У',
  container_replace: 'З',
  container_removal: 'Сн',
  waste_removal: 'ВМ',
  metal_removal: 'МЛ',
};

/** Минимальный объём вывоза мусора (м³). */
export const MIN_WASTE_VOLUME_M3 = 8;

// ── Тип заявки на технику ──
// Здесь, рядом с остальными перечнями, а не в vehicle-requests.ts: по типу заявки задан коридор
// ролей отдела (`allowedVehicleRequestTypes`, ADR 0040), а матрица прав лежит ниже по импортам —
// vehicle-requests.ts сам импортирует permissions.ts, и обратная ссылка замкнула бы круг.
export const VEHICLE_REQUEST_TYPES = ['special_equipment', 'freight_transport'] as const;
export const vehicleRequestTypeSchema = z.enum(VEHICLE_REQUEST_TYPES);
export type VehicleRequestType = (typeof VEHICLE_REQUEST_TYPES)[number];

export const vehicleRequestTypeLabels: Record<VehicleRequestType, string> = {
  special_equipment: 'Техника для работы на объекте',
  freight_transport: 'Грузоперевозка',
};

export const vehicleRequestTypeColors: Record<VehicleRequestType, string> = {
  special_equipment: 'geekblue',
  freight_transport: 'green',
};

// ── Вид записи справочника: контейнер или самосвал ──
export const CONTAINER_KINDS = ['cont', 'truck'] as const;
export const containerKindSchema = z.enum(CONTAINER_KINDS);
export type ContainerKind = (typeof CONTAINER_KINDS)[number];

export const containerKindLabels: Record<ContainerKind, string> = {
  cont: 'Контейнер',
  truck: 'Самосвал',
};

export const containerKindColors: Record<ContainerKind, string> = {
  cont: 'blue',
  truck: 'orange',
};

// ── Типы контейнеров и машин (единый справочник; данные в БД) ──
// Контейнер 25 м³ для тяжёлых грузов выведен из обихода (миграция 0041): в справочнике он
// остаётся неактивным — на него ссылаются заведённые заявки.
export const CONTAINER_TYPE_SEED = [
  { code: 'container_8', name: 'Контейнер 8 м³', sortOrder: 10, type: 'cont' },
  { code: 'container_20', name: 'Контейнер 20 м³', sortOrder: 20, type: 'cont' },
  { code: 'container_27', name: 'Контейнер 27 м³', sortOrder: 30, type: 'cont' },
  { code: 'container_38', name: 'Контейнер 38 м³', sortOrder: 40, type: 'cont' },
  { code: 'dump_truck_25', name: 'Самосвал 25 м³', sortOrder: 50, type: 'truck' },
  { code: 'dump_truck_36', name: 'Самосвал 36 м³', sortOrder: 60, type: 'truck' },
] as const;

// ── Статусы файлов ──
export const FILE_STATUSES = ['pending', 'active', 'deleted'] as const;
export type FileStatus = (typeof FILE_STATUSES)[number];
